import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import './InventoryReports.css';

const chartPalette = ['#f97316', '#0f766e', '#2563eb', '#e11d48', '#7c3aed', '#f59e0b'];

const buildDefaultFilters = () => {
  const today = new Date();
  const fromDate = new Date(today);
  fromDate.setDate(fromDate.getDate() - 30);

  return {
    fromDate: fromDate.toISOString().slice(0, 10),
    toDate: today.toISOString().slice(0, 10),
  };
};

const formatNumber = (value) => new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
}).format(Number(value || 0));

const formatMoney = (value) => `Tk ${formatNumber(value)}`;

const formatDateTime = (value) => {
  if (!value) {
    return '-';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

function PieChart({ data }) {
  const total = data.reduce((sum, item) => sum + Number(item.value || 0), 0);
  const radius = 48;
  const circumference = 2 * Math.PI * radius;

  if (!total) {
    return <div className="inventory-reports-empty-chart">No data</div>;
  }

  let runningOffset = 0;

  return (
    <svg viewBox="0 0 140 140" className="inventory-reports-pie" role="img" aria-label="Pie chart">
      <circle cx="70" cy="70" r={radius} fill="none" stroke="#e5edf5" strokeWidth="20" />
      <g transform="rotate(-90 70 70)">
        {data.map((item) => {
          const value = Number(item.value || 0);
          const arcLength = (value / total) * circumference;
          const segment = (
            <circle
              key={item.label}
              cx="70"
              cy="70"
              r={radius}
              fill="none"
              stroke={item.color}
              strokeWidth="20"
              strokeDasharray={`${arcLength} ${circumference}`}
              strokeDashoffset={-runningOffset}
            />
          );

          runningOffset += arcLength;
          return segment;
        })}
      </g>
      <text x="70" y="64" textAnchor="middle" className="inventory-reports-pie-total-label">Total</text>
      <text x="70" y="84" textAnchor="middle" className="inventory-reports-pie-total-value">{formatNumber(total)}</text>
    </svg>
  );
}

function ChartCard({ title, subtitle, data, valueFormatter = formatNumber }) {
  return (
    <article className="inventory-reports-chart-card">
      <div className="inventory-reports-card-header">
        <div>
          <h3>{title}</h3>
          <p>{subtitle}</p>
        </div>
      </div>
      <div className="inventory-reports-pie-layout">
        <PieChart data={data} />
        <div className="inventory-reports-legend">
          {data.length === 0 ? (
            <div className="inventory-reports-empty-state">No report rows for this chart.</div>
          ) : (
            data.map((item) => (
              <div key={item.label} className="inventory-reports-legend-item">
                <span className="inventory-reports-legend-color" style={{ backgroundColor: item.color }} />
                <div>
                  <strong>{item.label}</strong>
                  <span>{valueFormatter(item.value)}</span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </article>
  );
}

function BarListCard({ title, subtitle, rows, valueKey, labelKey = 'label', valueFormatter = formatNumber }) {
  const maxValue = Math.max(...rows.map((row) => Number(row[valueKey] || 0)), 1);

  return (
    <article className="inventory-reports-chart-card">
      <div className="inventory-reports-card-header">
        <div>
          <h3>{title}</h3>
          <p>{subtitle}</p>
        </div>
      </div>
      <div className="inventory-reports-bar-list">
        {rows.length === 0 ? (
          <div className="inventory-reports-empty-state">No report rows for this chart.</div>
        ) : (
          rows.map((row, index) => {
            const value = Number(row[valueKey] || 0);
            return (
              <div key={`${row[labelKey]}-${index}`} className="inventory-reports-bar-row">
                <div className="inventory-reports-bar-copy">
                  <strong>{row[labelKey]}</strong>
                  <span>{valueFormatter(value)}</span>
                </div>
                <div className="inventory-reports-bar-track">
                  <span
                    className="inventory-reports-bar-fill"
                    style={{
                      width: `${(value / maxValue) * 100}%`,
                      background: `linear-gradient(90deg, ${chartPalette[index % chartPalette.length]}, rgba(255,255,255,0.45))`,
                    }}
                  />
                </div>
              </div>
            );
          })
        )}
      </div>
    </article>
  );
}

function DataTableCard({ title, subtitle, columns, rows, emptyMessage }) {
  return (
    <article className="inventory-reports-table-card">
      <div className="inventory-reports-card-header">
        <div>
          <h3>{title}</h3>
          <p>{subtitle}</p>
        </div>
      </div>
      <div className="inventory-reports-table-wrap">
        {rows.length === 0 ? (
          <div className="inventory-reports-empty-state">{emptyMessage}</div>
        ) : (
          <table className="inventory-reports-table">
            <thead>
              <tr>
                {columns.map((column) => (
                  <th key={column.key}>{column.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={`${title}-${index}`}>
                  {columns.map((column) => (
                    <td key={column.key}>{column.render ? column.render(row) : row[column.key] || '-'}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </article>
  );
}

function InventoryReports({ onLogout }) {
  const navigate = useNavigate();
  const [filters, setFilters] = useState(buildDefaultFilters);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeReport, setActiveReport] = useState('all');

  const reportOptions = [
    { value: 'all', label: 'All Reports' },
    { value: 'stock', label: 'Stock Report' },
    { value: 'purchase', label: 'Purchase Report' },
    { value: 'issue', label: 'Issued Parts Report' },
    { value: 'movement', label: 'Purchase Vs Issue' },
  ];

  const fetchReports = useCallback(async (nextFilters = filters) => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/inventory-reports/dashboard', {
        params: nextFilters,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setReportData(response.data);
    } catch (reportError) {
      setError(reportError.response?.data?.message || 'Failed to load inventory reports.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchReports(filters);
  }, [fetchReports, filters]);

  const stockHealthChart = useMemo(() => {
    const summary = reportData?.inventory?.summary || {};
    const totalParts = Number(summary.totalParts || 0);
    const lowStockCount = Number(summary.lowStockCount || 0);
    const outOfStockCount = Number(summary.outOfStockCount || 0);
    const healthyCount = Math.max(totalParts - lowStockCount - outOfStockCount, 0);

    return [
      { label: 'Healthy stock', value: healthyCount, color: '#0f766e' },
      { label: 'Low stock', value: lowStockCount, color: '#f59e0b' },
      { label: 'Out of stock', value: outOfStockCount, color: '#dc2626' },
    ].filter((item) => item.value > 0);
  }, [reportData]);

  const deadStockChart = useMemo(() => {
    const summary = reportData?.inventory?.deadStock?.summary || reportData?.inventory?.summary || {};

    return [
      { label: 'No issue 6 months', value: Number(summary.deadStock6Count || 0), color: '#f59e0b' },
      { label: 'No issue 12 months', value: Number(summary.deadStock12Count || 0), color: '#ef4444' },
      { label: 'No issue 24 months', value: Number(summary.deadStock24Count || 0), color: '#7c3aed' },
    ].filter((item) => item.value > 0);
  }, [reportData]);

  const purchaseTypeChart = useMemo(() => (
    (reportData?.purchases?.typeBreakdown || []).map((item, index) => ({
      label: item.label,
      value: Number(item.totalAmount || 0),
      color: chartPalette[index % chartPalette.length],
    }))
  ), [reportData]);

  const issueReasonChart = useMemo(() => (
    (reportData?.issues?.reasonBreakdown || []).map((item, index) => ({
      label: item.label,
      value: Number(item.totalAmount || 0),
      color: chartPalette[index % chartPalette.length],
    }))
  ), [reportData]);

  const purchaseBrandChart = useMemo(() => (
    (reportData?.purchases?.brandBreakdown || []).map((item, index) => ({
      label: item.label,
      value: Number(item.totalAmount || 0),
      color: chartPalette[index % chartPalette.length],
    }))
  ), [reportData]);

  const issueBrandChart = useMemo(() => (
    (reportData?.issues?.brandBreakdown || []).map((item, index) => ({
      label: item.label,
      value: Number(item.totalAmount || 0),
      color: chartPalette[index % chartPalette.length],
    }))
  ), [reportData]);

  const inventorySummary = reportData?.inventory?.summary || {};
  const purchaseSummary = reportData?.purchases?.summary || {};
  const issueSummary = reportData?.issues?.summary || {};
  const reportRangeLabel = reportData?.filters
    ? `${reportData.filters.fromDate} to ${reportData.filters.toDate}`
    : `${filters.fromDate} to ${filters.toDate}`;
  const showSection = (sectionName) => activeReport === 'all' || activeReport === sectionName;

  return (
    <div className="home-container inventory-reports-page">
      <Sidebar onLogout={onLogout} />
      <div className="main-section inventory-reports-main">
        <div className="inventory-reports-container">
          <header className="inventory-reports-header">
            <div className="inventory-reports-title-group">
              <button className="app-back-button" type="button" onClick={() => navigate('/')}>
                Back
              </button>
              <div>
                <span className="inventory-reports-kicker">Report Button</span>
                <h1>Inventory & Parts Reports</h1>
                <p>VehicleParts stock, purchase_item buying, and IssuedParts usage together in one report dashboard.</p>
                <small className="inventory-reports-range-label">Showing report from {reportRangeLabel}</small>
              </div>
            </div>
            <div className="inventory-reports-header-actions">
              <button type="button" className="inventory-reports-secondary" onClick={() => navigate('/inventory')}>
                Open Inventory
              </button>
              <button type="button" className="btn-refresh" onClick={() => fetchReports(filters)} disabled={loading}>
                {loading ? 'Loading...' : 'Refresh'}
              </button>
            </div>
          </header>

          <section className="inventory-reports-toolbar">
            <div className="inventory-reports-toolbar-left">
              <div className="inventory-reports-selector-block">
                <label className="inventory-reports-selector-label" htmlFor="reportSelector">
                  Select report
                </label>
                <select
                  id="reportSelector"
                  className="inventory-reports-selector"
                  value={activeReport}
                  onChange={(event) => setActiveReport(event.target.value)}
                >
                  {reportOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <div className="inventory-reports-selector-tabs">
                  {reportOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={`inventory-reports-selector-tab${activeReport === option.value ? ' active' : ''}`}
                      onClick={() => setActiveReport(option.value)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="inventory-reports-filter-grid">
                <label>
                  <span>From date</span>
                  <input
                    type="date"
                    value={filters.fromDate}
                    onChange={(event) => setFilters((current) => ({ ...current, fromDate: event.target.value }))}
                  />
                </label>
                <label>
                  <span>To date</span>
                  <input
                    type="date"
                    value={filters.toDate}
                    onChange={(event) => setFilters((current) => ({ ...current, toDate: event.target.value }))}
                  />
                </label>
                <button type="button" className="inventory-reports-apply" onClick={() => fetchReports(filters)} disabled={loading}>
                  Apply Report
                </button>
              </div>
            </div>
            <div className="inventory-reports-toolbar-note">
              <strong>Live stock snapshot</strong>
              <span>VehicleParts stock is shown as current stock. Purchase and issue sections follow the selected date range.</span>
            </div>
          </section>

          {error && <div className="inventory-reports-feedback inventory-reports-feedback-error">{error}</div>}
          {loading && !reportData ? <div className="inventory-reports-feedback">Loading report dashboard...</div> : null}

          {reportData ? (
            <>
              {showSection('stock') ? (
              <section className="inventory-reports-section inventory-reports-section-stock">
                <div className="inventory-reports-section-heading">
                  <div>
                    <span className="inventory-reports-section-tag">VehicleParts</span>
                    <h2>Stock Report</h2>
                    <p>Current inventory position, stock value, low stock signals, and where parts are sitting.</p>
                  </div>
                </div>

                <div className="inventory-reports-summary-grid">
                  <article>
                    <span>Total Parts</span>
                    <strong>{formatNumber(inventorySummary.totalParts)}</strong>
                  </article>
                  <article>
                    <span>Total Stock Qty</span>
                    <strong>{formatNumber(inventorySummary.totalStockQty)}</strong>
                  </article>
                  <article>
                    <span>Stock Value</span>
                    <strong>{formatMoney(inventorySummary.totalStockValue)}</strong>
                  </article>
                  <article>
                    <span>Low / Out</span>
                    <strong>{formatNumber(inventorySummary.lowStockCount)} / {formatNumber(inventorySummary.outOfStockCount)}</strong>
                  </article>
                  <article>
                    <span>Deadstock 6M</span>
                    <strong>{formatNumber(inventorySummary.deadStock6Count)}</strong>
                  </article>
                  <article>
                    <span>Deadstock 12M</span>
                    <strong>{formatNumber(inventorySummary.deadStock12Count)}</strong>
                  </article>
                  <article>
                    <span>Deadstock 24M</span>
                    <strong>{formatNumber(inventorySummary.deadStock24Count)}</strong>
                  </article>
                </div>

                <div className="inventory-reports-chart-grid">
                  <ChartCard
                    title="Stock Health Pie"
                    subtitle="Parts grouped by healthy, low, and out-of-stock status."
                    data={stockHealthChart}
                  />
                  <ChartCard
                    title="Deadstock Pie"
                    subtitle="Part numbers not issued in the last 6, 12, and 24 months."
                    data={deadStockChart}
                  />
                  <BarListCard
                    title="Stock Value by Location"
                    subtitle="Highest-value storage locations in the current inventory."
                    rows={reportData.inventory.locationBreakdown || []}
                    valueKey="totalValue"
                    valueFormatter={formatMoney}
                  />
                  <ChartCard
                    title="Stock Value by Part Type"
                    subtitle="How current stock value is divided across part categories."
                    data={(reportData.inventory.typeBreakdown || []).map((item, index) => ({
                      label: item.label,
                      value: Number(item.totalValue || 0),
                      color: chartPalette[index % chartPalette.length],
                    }))}
                    valueFormatter={formatMoney}
                  />
                </div>

                <div className="inventory-reports-table-grid">
                  <DataTableCard
                    title="Top Stock Items"
                    subtitle="Highest stock quantity items in VehicleParts."
                    rows={reportData.inventory.topStockItems || []}
                    emptyMessage="No stock items found."
                    columns={[
                      { key: 'partName', label: 'Part Name' },
                      { key: 'partNo', label: 'Part No' },
                      { key: 'partType', label: 'Type' },
                      { key: 'qty', label: 'Qty', render: (row) => formatNumber(row.qty) },
                      { key: 'stockValue', label: 'Value', render: (row) => formatMoney(row.stockValue) },
                    ]}
                  />
                  <DataTableCard
                    title="Low Stock Items"
                    subtitle="Fast view of stock that needs attention."
                    rows={reportData.inventory.lowStockItems || []}
                    emptyMessage="No low stock rows for the current inventory snapshot."
                    columns={[
                      { key: 'partName', label: 'Part Name' },
                      { key: 'partNo', label: 'Part No' },
                      { key: 'location', label: 'Location' },
                      { key: 'qty', label: 'Qty', render: (row) => formatNumber(row.qty) },
                      { key: 'reorderLevel', label: 'Reorder', render: (row) => row.reorderLevel ? formatNumber(row.reorderLevel) : '-' },
                    ]}
                  />
                  <DataTableCard
                    title="Deadstock 6 Months"
                    subtitle="Part numbers with stock on hand but no issue in the last 6 months."
                    rows={reportData.inventory.deadStock?.sixMonths || []}
                    emptyMessage="No deadstock parts found for the last 6 months."
                    columns={[
                      { key: 'partNo', label: 'Part No' },
                      { key: 'partName', label: 'Part Name' },
                      { key: 'brand', label: 'Brand' },
                      { key: 'qty', label: 'Qty', render: (row) => formatNumber(row.qty) },
                      { key: 'lastIssueDate', label: 'Last Issue', render: (row) => formatDateTime(row.lastIssueDate) },
                      { key: 'stockValue', label: 'Stock Value', render: (row) => formatMoney(row.stockValue) },
                    ]}
                  />
                  <DataTableCard
                    title="Deadstock 12 Months"
                    subtitle="Part numbers with stock on hand but no issue in the last 12 months."
                    rows={reportData.inventory.deadStock?.twelveMonths || []}
                    emptyMessage="No deadstock parts found for the last 12 months."
                    columns={[
                      { key: 'partNo', label: 'Part No' },
                      { key: 'partName', label: 'Part Name' },
                      { key: 'brand', label: 'Brand' },
                      { key: 'qty', label: 'Qty', render: (row) => formatNumber(row.qty) },
                      { key: 'lastIssueDate', label: 'Last Issue', render: (row) => formatDateTime(row.lastIssueDate) },
                      { key: 'stockValue', label: 'Stock Value', render: (row) => formatMoney(row.stockValue) },
                    ]}
                  />
                  <DataTableCard
                    title="Deadstock 24 Months"
                    subtitle="Part numbers with stock on hand but no issue in the last 24 months."
                    rows={reportData.inventory.deadStock?.twentyFourMonths || []}
                    emptyMessage="No deadstock parts found for the last 24 months."
                    columns={[
                      { key: 'partNo', label: 'Part No' },
                      { key: 'partName', label: 'Part Name' },
                      { key: 'brand', label: 'Brand' },
                      { key: 'qty', label: 'Qty', render: (row) => formatNumber(row.qty) },
                      { key: 'lastIssueDate', label: 'Last Issue', render: (row) => formatDateTime(row.lastIssueDate) },
                      { key: 'stockValue', label: 'Stock Value', render: (row) => formatMoney(row.stockValue) },
                    ]}
                  />
                </div>
              </section>
              ) : null}

              {showSection('purchase') ? (
              <section className="inventory-reports-section inventory-reports-section-purchase">
                <div className="inventory-reports-section-heading">
                  <div>
                    <span className="inventory-reports-section-tag">purchase_item</span>
                    <h2>Purchase Report</h2>
                    <p>From date to date purchase report with part number wise, brand wise, vendor wise, and daily buying analysis.</p>
                  </div>
                </div>

                <div className="inventory-reports-summary-grid">
                  <article>
                    <span>Transactions</span>
                    <strong>{formatNumber(purchaseSummary.transactionCount)}</strong>
                  </article>
                  <article>
                    <span>Purchased Qty</span>
                    <strong>{formatNumber(purchaseSummary.totalQty)}</strong>
                  </article>
                  <article>
                    <span>Purchase Amount</span>
                    <strong>{formatMoney(purchaseSummary.totalAmount)}</strong>
                  </article>
                  <article>
                    <span>Avg Unit Price</span>
                    <strong>{formatMoney(purchaseSummary.averageUnitPrice)}</strong>
                  </article>
                </div>

                <div className="inventory-reports-chart-grid">
                  <ChartCard
                    title="Purchase Amount by Part Type"
                    subtitle="Pie chart of spending by purchased part categories."
                    data={purchaseTypeChart}
                    valueFormatter={formatMoney}
                  />
                  <ChartCard
                    title="Purchase Amount by Brand"
                    subtitle="Brand wise purchase report for the selected dates."
                    data={purchaseBrandChart}
                    valueFormatter={formatMoney}
                  />
                  <BarListCard
                    title="Top Vendors by Purchase Amount"
                    subtitle="Which vendors account for the most purchase value."
                    rows={reportData.purchases.vendorBreakdown || []}
                    valueKey="totalAmount"
                    valueFormatter={formatMoney}
                  />
                  <BarListCard
                    title="Date Wise Purchase Amount"
                    subtitle="Daily purchase total from the selected date range."
                    rows={reportData.purchases.dailyBreakdown || []}
                    valueKey="totalAmount"
                    valueFormatter={formatMoney}
                  />
                </div>

                <div className="inventory-reports-table-grid">
                  <DataTableCard
                    title="Recent Purchase Rows"
                    subtitle="Latest rows from the selected date range."
                    rows={reportData.purchases.recentPurchases || []}
                    emptyMessage="No purchase rows found for the selected dates."
                    columns={[
                      { key: 'grnNo', label: 'GRN' },
                      { key: 'purchaseDate', label: 'Date', render: (row) => formatDateTime(row.purchaseDate) },
                      { key: 'partNo', label: 'Part No' },
                      { key: 'vendorName', label: 'Vendor' },
                      { key: 'qty', label: 'Qty', render: (row) => formatNumber(row.qty) },
                      { key: 'totalAmount', label: 'Total', render: (row) => formatMoney(row.totalAmount) },
                    ]}
                  />
                  <DataTableCard
                    title="Part Number Wise Purchase Report"
                    subtitle="Purchase qty, amount, and average unit price by part number."
                    rows={reportData.purchases.topPurchasedItems || []}
                    emptyMessage="No grouped purchase rows found."
                    columns={[
                      { key: 'partNo', label: 'Part No' },
                      { key: 'brand', label: 'Brand' },
                      { key: 'partType', label: 'Type' },
                      { key: 'transactionCount', label: 'Txns', render: (row) => formatNumber(row.transactionCount) },
                      { key: 'totalQty', label: 'Qty', render: (row) => formatNumber(row.totalQty) },
                      { key: 'averageUnitPrice', label: 'Avg Price', render: (row) => formatMoney(row.averageUnitPrice) },
                      { key: 'totalAmount', label: 'Amount', render: (row) => formatMoney(row.totalAmount) },
                    ]}
                  />
                  <DataTableCard
                    title="Brand Wise Purchase Report"
                    subtitle="Purchase totals grouped by brand in the selected date range."
                    rows={reportData.purchases.brandBreakdown || []}
                    emptyMessage="No brand wise purchase rows found."
                    columns={[
                      { key: 'label', label: 'Brand' },
                      { key: 'transactionCount', label: 'Txns', render: (row) => formatNumber(row.transactionCount) },
                      { key: 'totalQty', label: 'Qty', render: (row) => formatNumber(row.totalQty) },
                      { key: 'totalAmount', label: 'Amount', render: (row) => formatMoney(row.totalAmount) },
                    ]}
                  />
                </div>
              </section>
              ) : null}

              {showSection('issue') ? (
              <section className="inventory-reports-section inventory-reports-section-issue">
                <div className="inventory-reports-section-heading">
                  <div>
                    <span className="inventory-reports-section-tag">IssuedParts</span>
                    <h2>Issued Parts Report</h2>
                    <p>From date to date issue report with part number wise, brand wise, recipient wise, and daily issue analysis.</p>
                  </div>
                </div>

                <div className="inventory-reports-summary-grid">
                  <article>
                    <span>Issue Rows</span>
                    <strong>{formatNumber(issueSummary.transactionCount)}</strong>
                  </article>
                  <article>
                    <span>Issued Qty</span>
                    <strong>{formatNumber(issueSummary.totalIssuedQty)}</strong>
                  </article>
                  <article>
                    <span>Issued Amount</span>
                    <strong>{formatMoney(issueSummary.totalIssuedAmount)}</strong>
                  </article>
                  <article>
                    <span>References</span>
                    <strong>{formatNumber(issueSummary.referenceCount)}</strong>
                  </article>
                  <article>
                    <span>Recipients</span>
                    <strong>{formatNumber(issueSummary.recipientCount)}</strong>
                  </article>
                </div>

                <div className="inventory-reports-chart-grid">
                  <ChartCard
                    title="Issued Amount by Reason"
                    subtitle="Pie chart for issued amount grouped by reason."
                    data={issueReasonChart}
                    valueFormatter={formatMoney}
                  />
                  <ChartCard
                    title="Issued Amount by Brand"
                    subtitle="Brand wise issue amount for the selected dates."
                    data={issueBrandChart}
                    valueFormatter={formatMoney}
                  />
                  <BarListCard
                    title="Top Recipients by Issued Amount"
                    subtitle="Recipients with the highest issued amount."
                    rows={reportData.issues.recipientBreakdown || []}
                    valueKey="totalAmount"
                    valueFormatter={formatMoney}
                  />
                  <BarListCard
                    title="Date Wise Issued Amount"
                    subtitle="Daily issue amount from the selected date range."
                    rows={reportData.issues.dailyBreakdown || []}
                    valueKey="totalAmount"
                    valueFormatter={formatMoney}
                  />
                </div>

                <div className="inventory-reports-table-grid">
                  <DataTableCard
                    title="Recent Issued Rows"
                    subtitle="Latest issued parts rows within the selected dates."
                    rows={reportData.issues.recentIssues || []}
                    emptyMessage="No issued parts rows found for the selected dates."
                    columns={[
                      { key: 'IssueDate', label: 'Date', render: (row) => formatDateTime(row.IssueDate) },
                      { key: 'PartNo', label: 'Part No' },
                      { key: 'IssuedTo', label: 'Issued To' },
                      { key: 'Reason', label: 'Reason' },
                      { key: 'Quantity', label: 'Qty', render: (row) => formatNumber(row.Quantity) },
                      { key: 'unitCost', label: 'Unit Cost', render: (row) => formatMoney(row.unitCost) },
                      { key: 'totalAmount', label: 'Amount', render: (row) => formatMoney(row.totalAmount) },
                    ]}
                  />
                  <DataTableCard
                    title="Part Number Wise Issued Report"
                    subtitle="Issued qty, amount, and transactions grouped by part number."
                    rows={reportData.issues.topIssuedItems || []}
                    emptyMessage="No grouped issue rows found."
                    columns={[
                      { key: 'partNo', label: 'Part No' },
                      { key: 'partName', label: 'Part Name' },
                      { key: 'brand', label: 'Brand' },
                      { key: 'transactionCount', label: 'Txns', render: (row) => formatNumber(row.transactionCount) },
                      { key: 'totalQty', label: 'Qty', render: (row) => formatNumber(row.totalQty) },
                      { key: 'totalAmount', label: 'Amount', render: (row) => formatMoney(row.totalAmount) },
                    ]}
                  />
                  <DataTableCard
                    title="Brand Wise Issued Report"
                    subtitle="Issued quantity and amount grouped by brand in the selected date range."
                    rows={reportData.issues.brandBreakdown || []}
                    emptyMessage="No brand wise issued rows found."
                    columns={[
                      { key: 'label', label: 'Brand' },
                      { key: 'transactionCount', label: 'Txns', render: (row) => formatNumber(row.transactionCount) },
                      { key: 'totalQty', label: 'Qty', render: (row) => formatNumber(row.totalQty) },
                      { key: 'totalAmount', label: 'Amount', render: (row) => formatMoney(row.totalAmount) },
                    ]}
                  />
                </div>
              </section>
              ) : null}

              {showSection('movement') ? (
              <section className="inventory-reports-section inventory-reports-section-movement">
                <div className="inventory-reports-section-heading">
                  <div>
                    <span className="inventory-reports-section-tag">AI Comparison</span>
                    <h2>Purchase Vs Issue Reports</h2>
                    <p>Side-by-side movement reports so you can compare what was purchased and what was issued in the same date range.</p>
                  </div>
                </div>

                <div className="inventory-reports-table-grid">
                  <DataTableCard
                    title="Part Number Wise Purchase Vs Issue"
                    subtitle="Compare purchased qty, issued qty, and remaining movement by part number."
                    rows={reportData.movement?.byPartNo || []}
                    emptyMessage="No movement comparison rows found."
                    columns={[
                      { key: 'partNo', label: 'Part No' },
                      { key: 'brand', label: 'Brand' },
                      { key: 'purchasedQty', label: 'Purchased', render: (row) => formatNumber(row.purchasedQty) },
                      { key: 'issuedQty', label: 'Issued', render: (row) => formatNumber(row.issuedQty) },
                      { key: 'balanceQty', label: 'Balance', render: (row) => formatNumber(row.balanceQty) },
                      { key: 'purchasedAmount', label: 'Purchase Amount', render: (row) => formatMoney(row.purchasedAmount) },
                    ]}
                  />
                  <DataTableCard
                    title="Brand Wise Purchase Vs Issue"
                    subtitle="Compare total purchased qty and issued qty by brand."
                    rows={reportData.movement?.byBrand || []}
                    emptyMessage="No brand comparison rows found."
                    columns={[
                      { key: 'brand', label: 'Brand' },
                      { key: 'purchasedQty', label: 'Purchased', render: (row) => formatNumber(row.purchasedQty) },
                      { key: 'issuedQty', label: 'Issued', render: (row) => formatNumber(row.issuedQty) },
                      { key: 'balanceQty', label: 'Balance', render: (row) => formatNumber(row.balanceQty) },
                      { key: 'purchasedAmount', label: 'Purchase Amount', render: (row) => formatMoney(row.purchasedAmount) },
                    ]}
                  />
                </div>
              </section>
              ) : null}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default InventoryReports;
